/**
 * Thème « Selon l'heure » : sombre entre le coucher et le lever du soleil.
 *
 * Les heures suivent la saison (le soleil ne se couche pas à la même heure en
 * juin et en décembre) sans jamais demander la position de l'appareil : le
 * calcul prend un point fixe au centre de la France métropolitaine. À l'échelle
 * d'un changement de thème, l'écart avec la position réelle est négligeable.
 */
export type Theme = 'clair' | 'sombre' | 'auto' | 'heure';
export const THEMES: readonly Theme[] = ['clair', 'sombre', 'auto', 'heure'];
/** Thème de qui n'a jamais choisi : « Selon l'heure » (propriétaire, 24/10/2026). */
export const DEFAULT_THEME: Theme = 'heure';

const LATITUDE = 46.6;
const LONGITUDE = 2.5;
const RAD = Math.PI / 180;

/** Lever et coucher du soleil (minutes UTC depuis minuit) pour ce jour. */
export function sunTimesUtc(date: Date): { sunrise: number; sunset: number } {
  const start = Date.UTC(date.getUTCFullYear(), 0, 0);
  const dayOfYear = Math.floor((date.getTime() - start) / 86_400_000);
  const gamma = (2 * Math.PI / 365) * (dayOfYear - 1);
  // Équation du temps (minutes) et déclinaison solaire (radians) — série NOAA.
  const equation = 229.18 * (0.000075 + 0.001868 * Math.cos(gamma) - 0.032077 * Math.sin(gamma)
    - 0.014615 * Math.cos(2 * gamma) - 0.040849 * Math.sin(2 * gamma));
  const declination = 0.006918 - 0.399912 * Math.cos(gamma) + 0.070257 * Math.sin(gamma)
    - 0.006758 * Math.cos(2 * gamma) + 0.000907 * Math.sin(2 * gamma)
    - 0.002697 * Math.cos(3 * gamma) + 0.00148 * Math.sin(3 * gamma);
  const hourAngle = Math.acos(
    Math.cos(90.833 * RAD) / (Math.cos(LATITUDE * RAD) * Math.cos(declination))
      - Math.tan(LATITUDE * RAD) * Math.tan(declination),
  ) / RAD;
  const noon = 720 - 4 * LONGITUDE - equation;
  return { sunrise: noon - 4 * hourAngle, sunset: noon + 4 * hourAngle };
}

/** Vrai entre le coucher et le lever du soleil. */
export function isNight(date: Date): boolean {
  const minutes = date.getUTCHours() * 60 + date.getUTCMinutes();
  const { sunrise, sunset } = sunTimesUtc(date);
  return minutes < sunrise || minutes >= sunset;
}

/** Valeur posée sur `data-theme` : « heure » se résout en clair ou sombre. */
export function resolvedTheme(theme: Theme, now: Date): 'clair' | 'sombre' | 'auto' {
  if (theme === 'heure') return isNight(now) ? 'sombre' : 'clair';
  return theme;
}
